import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import NavBar from "../components/NavBar/NavBar";
import { CancelIcon, InfoIcon, MealPlanIcon } from "../assets/icons/icons";
import { CalorieIcon, ProteinIcon, CarbIcon, FatIcon } from "../assets/icons/icons";
import LoadingAnimation from "../components/LoadingAnimation/LoadingAnimation";
import "../components/MealModal/style.css";
import mealPlanIllustration from "../assets/meal-plan.jpg";
import { apiFetch } from "../api/apiClient";
import { fetchMealPreferences, updateMealPreferences } from "../api/mealPreferences";
import { PROTEIN_OPTIONS, CARB_OPTIONS, FAT_OPTIONS } from "../constants/nutritionOptions";
import { deriveMealCountGuidance, MEAL_COUNT_LIMITS } from "../utils/mealPreferences";

const PLAN_CATEGORY_FROM_UI = {
  protein: "protein",
  carbs: "carb",
  fruitsVeggies: "fruit_veg",
  fats: "fat",
};
const MIN_PREFERENCE_SELECTIONS = 4;
const PREFERENCE_SELECTION_SUBTEXT = "Min 4 selections required to maintain healthy nutrient balance.";
const MEAL_LOADING_STEPS = [
  "Reviewing your goals, schedule, and calorie target...",
  "Balancing protein, carbs, and fats for steady energy...",
  "Spacing meals to avoid crashes and late-night binges...",
  "Pairing your preferred proteins with smart carb sides...",
  "Adding fiber, greens, and hydration cues to every block...",
  "Keeping fats calibrated so protein stays the hero...",
  "Creating simple swaps for foods you don't love...",
  "Checking portions so you're satisfied, not stuffed...",
  "Finalizing a grocery-friendly plan you can follow today...",
];

const MealLoadingOverlay = ({ message }) => (
  <div className="meal-loading-overlay">
    <div className="loading-animation-wrapper fullscreen with-message">
      <div className="loader" />
      <p className="plan-loading-message">
        {message || "Building your personalized meal plan..."}
      </p>
    </div>
  </div>
);

const numberOrZero = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? Math.round(num) : 0;
};

const roundedOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
};


const mealCaloriesFromMacros = (macros = {}) => {
  const protein = Number(macros.protein) || 0;
  const carbs = Number(macros.carbs) || 0;
  const fats = Number(macros.fats) || 0;
  return numberOrZero(protein * 4 + carbs * 4 + fats * 9);
};

const mealMacroSummary = (macros = {}) => {
  if (!macros) return [];
  const sections = [
    { key: "protein", short: "P", value: numberOrZero(macros.protein) },
    { key: "carbs", short: "C", value: numberOrZero(macros.carbs) },
    { key: "fats", short: "F", value: numberOrZero(macros.fats) },
  ];
  return sections.filter((section) => section.value > 0);
};

const pickRandomSubset = (options = [], count = 3) => {
  if (!Array.isArray(options) || !options.length) return [];
  const shuffled = options.slice();
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.max(0, count));
};

const convertUnitToGrams = (value, unit) => {
  if (!Number.isFinite(value)) return null;
  switch ((unit || "").toLowerCase()) {
    case "oz":
    case "ounce":
    case "ounces":
      return value * 28.3495;
    case "lb":
    case "lbs":
    case "pound":
    case "pounds":
      return value * 453.592;
    case "kg":
    case "kilogram":
    case "kilograms":
      return value * 1000;
    default:
      return null;
  }
};

const formatAmountInGrams = (amount) => {
  if (amount === null || amount === undefined) return "-";
  if (typeof amount === "number") return `${numberOrZero(amount)}g`;
  const raw = String(amount).trim();
  if (!raw) return "-";
  if (/\bgrams?\b/i.test(raw) || /\d+(?:\.\d+)?\s*g\b/i.test(raw)) {
    return raw
      .replace(/\bgrams?\b/gi, "g")
      .replace(/\s*g\b/gi, "g");
  }
  const unitMatch = raw.match(/^(\d+(?:\.\d+)?)\s*(oz|ounce|ounces|lb|lbs|pound|pounds|kg|kilogram|kilograms)$/i);
  if (unitMatch) {
    const value = parseFloat(unitMatch[1]);
    const unit = unitMatch[2];
    const grams = convertUnitToGrams(value, unit);
    if (grams !== null) {
      return `${numberOrZero(grams)}g`;
    }
  }
  const numericValue = Number(raw);
  if (Number.isFinite(numericValue)) {
    return `${numberOrZero(numericValue)}g`;
  }
  return raw;
};

const parseAmountToGrams = (amount) => {
  if (amount === null || amount === undefined) return null;
  if (typeof amount === "number") {
    return Number.isFinite(amount) ? amount : null;
  }
  const text = String(amount).trim();
  if (!text) return null;
  const gramMatch = text.match(/(\d+(?:\.\d+)?)\s*g\b/i);
  if (gramMatch) {
    return parseFloat(gramMatch[1]);
  }
  const unitMatch = text.match(/(\d+(?:\.\d+)?)(?:\s*)(oz|ounce|ounces|lb|lbs|pound|pounds|kg|kilogram|kilograms)/i);
  if (unitMatch) {
    return convertUnitToGrams(parseFloat(unitMatch[1]), unitMatch[2]);
  }
  const numericValue = Number(text);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeAmountUnit = (value = "") => {
  const cleaned = String(value || "")
    .toLowerCase()
    .replace(/[^a-z\s-]/g, " ")
    .trim();
  if (!cleaned) return "";
  if (/\begg(s)?\b/.test(cleaned)) return "egg";
  const token = cleaned.split(/\s+/).find(Boolean) || "";
  if (!token) return "";
  if (token.endsWith("s") && token.length > 1) return token.slice(0, -1);
  return token;
};

const parseAmountWithUnit = (amount) => {
  if (amount === null || amount === undefined) return null;
  if (typeof amount === "number") {
    return Number.isFinite(amount) ? { value: amount, unit: "" } : null;
  }
  const text = String(amount).trim();
  if (!text) return null;
  const match = text.match(/^([\d.]+)\s*([a-zA-Z][a-zA-Z\s-]*)?$/);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value)) return null;
  return {
    value,
    unit: normalizeAmountUnit(match[2] || ""),
  };
};

const ensureAmountLabel = (amount) => {
  if (!amount) return "-";
  const text = String(amount).trim();
  if (text === "-") return "-";
  if (!text) return "-";
  if (/g$/i.test(text)) return text;
  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) {
    return `${numberOrZero(numericValue)}g`;
  }
  return `${text}g`;
};

const deriveSwapAmountLabel = (swap) => {
  if (!swap) return "";
  if (swap.qty && swap.qty !== "-") return ensureAmountLabel(swap.qty);
  if (swap.replacement_grams != null) return ensureAmountLabel(`${swap.replacement_grams}g`);
  if (swap.amount) return ensureAmountLabel(swap.amount);
  if (swap.nutritionAmount) return ensureAmountLabel(swap.nutritionAmount);
  return "";
};

const formatMacroSummaryWithAmount = (macros, amountLabel = "") => {
  if (!macros) return "";
  const line = `${numberOrZero(macros.protein_g)}P / ${numberOrZero(macros.carbs_g)}C / ${numberOrZero(macros.fats_g)}F`;
  return amountLabel ? `${line} (per ${amountLabel})` : line;
};

const METADATA_LABELS = {
  category: "Category",
  name: "Food name",
  serving_size: "Serving size",
  calories: "Calories",
  protein_g: "Protein",
  carbs_g: "Carbs",
  fats_g: "Fat",
  fat_g: "Fat",
  fiber_g: "Fiber",
  sodium_mg: "Sodium",
  potassium_mg: "Potassium",
  complex_carb: "Complex carb",
  fatty_protein: "Fatty protein",
  lean_protein: "Lean protein",
  fruit: "Fruit",
  green_veggie: "Green veggie",
};

const METADATA_SORT_ORDER = [
  "category",
  "name",
  "serving_size",
  "calories",
  "protein_g",
  "carbs_g",
  "fats_g",
  "fat_g",
  "fiber_g",
  "sodium_mg",
  "potassium_mg",
  "complex_carb",
  "fatty_protein",
  "lean_protein",
  "fruit",
  "green_veggie",
  "sheet_name",
];

const formatMetadataValue = (key, value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const numericValue =
    typeof value === "number" || (typeof value === "string" && value.trim())
      ? roundedOrNull(value)
      : null;
  if (numericValue !== null) {
    if (/_mg$/i.test(key)) return `${numericValue} mg`;
    if (/_g$/i.test(key)) return `${numericValue} g`;
    if (key === "calories" || key === "kcal" || key === "energy") {
      return `${numericValue} kcal`;
    }
    return `${numericValue}`;
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (key === "category") {
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }
  return trimmed;
};

const buildMetadataEntries = (metadata = null) => {
  if (!metadata || typeof metadata !== "object") return [];
  const seen = new Set();
  const entries = Object.entries(metadata)
    .map(([key, value]) => {
      if (key === "sheet_name") return null;
      if (seen.has(key)) return null;
      const formattedValue = formatMetadataValue(key, value);
      if (!formattedValue) return null;
      seen.add(key);
      return {
        key,
        label: METADATA_LABELS[key] || key.replace(/_/g, " "),
        value: formattedValue,
      };
    })
    .filter(Boolean);
  return entries.sort((a, b) => {
    const aIdx = METADATA_SORT_ORDER.indexOf(a.key);
    const bIdx = METADATA_SORT_ORDER.indexOf(b.key);
    if (aIdx === -1 && bIdx === -1) return a.label.localeCompare(b.label);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
};

const deriveNutritionFromMetadata = (metadata = null) => {
  if (!metadata || typeof metadata !== "object") return null;
  const macros = {
    calories: metadata.calories ?? metadata.kcal ?? metadata.energy ?? null,
    protein_g: metadata.protein_g ?? metadata.protein ?? null,
    carbs_g: metadata.carbs_g ?? metadata.carbohydrates ?? metadata.carbs ?? null,
    fats_g: metadata.fats_g ?? metadata.fat_g ?? metadata.fat ?? null,
  };
  const normalized = {};
  Object.entries(macros).forEach(([key, value]) => {
    const normalizedValue = roundedOrNull(value);
    if (normalizedValue !== null) {
      normalized[key] = normalizedValue;
    }
  });
  return Object.keys(normalized).length ? normalized : null;
};

const normalizeNutrition = (macros = null, metadata = null) => {
  const derived = deriveNutritionFromMetadata(metadata);
  if (!macros && !derived) return null;
  const merged = { ...(derived || {}), ...(macros || {}) };
  const hasValue = Object.values(merged).some((val) => val !== null && val !== undefined);
  return hasValue ? merged : null;
};

const scaleNutritionForAmount = (macros = null, servedAmount = null, baseAmount = null) => {
  if (!macros) return null;
  const scaled = { ...macros };
  const served = parseAmountToGrams(servedAmount);
  const base = parseAmountToGrams(baseAmount);
  let ratio = null;
  if (Number.isFinite(served) && Number.isFinite(base) && base > 0) {
    ratio = served / base;
  } else {
    const servedCount = parseAmountWithUnit(servedAmount);
    const baseCount = parseAmountWithUnit(baseAmount);
    if (
      servedCount
      && baseCount
      && baseCount.value > 0
      && servedCount.unit
      && baseCount.unit
      && servedCount.unit === baseCount.unit
    ) {
      ratio = servedCount.value / baseCount.value;
    }
  }
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return scaled;
  }
  if (!Number.isFinite(ratio) || ratio <= 0) return scaled;
  ["calories", "protein_g", "carbs_g", "fats_g"].forEach((key) => {
    if (scaled[key] != null) {
      const value = Number(scaled[key]);
      if (Number.isFinite(value)) {
        scaled[key] = Math.round(value * ratio * 10) / 10;
      }
    }
  });
  return scaled;
};
const buildFoodContext = (entity) => {
  if (!entity) return null;
  const metadata =
    entity.info?.metadata ||
    entity.nutrition_metadata ||
    entity.metadata ||
    entity.nutrition_item?.metadata ||
    entity.info?.itemModel?.metadata ||
    entity.itemModel?.metadata ||
    null;
  const baseNutritionSource =
    entity.info?.nutrition ||
    entity.nutrition ||
    entity.nutrition_macros ||
    entity.info?.itemModel?.macros ||
    entity.itemModel?.macros ||
    entity.nutrition_item?.macros ||
    null;
  const baseNutrition = normalizeNutrition(baseNutritionSource, metadata);
  if (!baseNutrition) return null;
  const baseAmount =
    entity.info?.nutritionAmount ||
    entity.nutrition_amount ||
    entity.nutrition_item?.amount ||
    entity.info?.itemModel?.amount ||
    entity.itemModel?.amount ||
    metadata?.serving_size ||
    null;
  const servedAmount = entity.amount || entity.qty || entity.quantity || null;
  const macros = scaleNutritionForAmount(baseNutrition, servedAmount, baseAmount);
  return {
    macros,
    servedAmount,
  };
};

const applySwapTargetAdjustment = (swapEntry, originalContext, baseNutrition, baseAmount) => {
  if (!swapEntry || !originalContext || !baseNutrition) return swapEntry;
  const preservedKey = swapEntry.preserved_macro || null;
  if (!preservedKey) return swapEntry;
  if (preservedKey === "serving_size") {
    const servedGrams = parseAmountToGrams(originalContext.servedAmount);
    if (!Number.isFinite(servedGrams) || servedGrams <= 0) return swapEntry;
    const scaledMacros = scaleNutritionForAmount(baseNutrition, `${servedGrams}g`, baseAmount);
    return {
      ...swapEntry,
      qty: formatAmountInGrams(servedGrams),
      replacement_grams: servedGrams,
      macros: scaledMacros,
    };
  }
  const targetValue = Number(originalContext.macros?.[preservedKey]);
  if (!Number.isFinite(targetValue) || targetValue <= 0) return swapEntry;
  const baseMacroValue = Number(baseNutrition[preservedKey]);
  const baseGrams = parseAmountToGrams(baseAmount);
  if (!Number.isFinite(baseMacroValue) || baseMacroValue <= 0 || !Number.isFinite(baseGrams) || baseGrams <= 0) {
    return swapEntry;
  }
  const macroPerGram = baseMacroValue / baseGrams;
  if (!Number.isFinite(macroPerGram) || macroPerGram <= 0) return swapEntry;
  const requiredGrams = Math.max(1, Math.round(targetValue / macroPerGram));
  const scaledMacros = scaleNutritionForAmount(baseNutrition, `${requiredGrams}g`, baseAmount);
  return {
    ...swapEntry,
    qty: formatAmountInGrams(requiredGrams),
    replacement_grams: requiredGrams,
    macros: scaledMacros,
  };
};

const normalizeSwapSuggestion = (swap, originalContext = null) => {
  if (!swap || typeof swap !== "object") return null;
  const hasStructuredFields = "replacement_name" in swap || "replacement_grams" in swap || "preserved_macro" in swap;
  const itemModel = swap.item_model || swap.itemModel || null;
  const metadata = swap.metadata || itemModel?.metadata || null;
  const baseNutrition = normalizeNutrition(itemModel?.macros || null, metadata);
  const baseAmount = swap.nutrition_amount || itemModel?.amount || metadata?.serving_size || null;
  if (hasStructuredFields) {
    const gramsValue =
      swap.replacement_grams != null
        ? Number(swap.replacement_grams)
        : null;
    const formattedQty = gramsValue != null
      ? formatAmountInGrams(gramsValue)
      : swap.qty
        ? formatAmountInGrams(swap.qty)
        : swap.amount
          ? formatAmountInGrams(swap.amount)
          : "";
    const qty = formattedQty === "-" ? "" : formattedQty;
    const result = {
      item: swap.replacement_name || swap.item || "",
      qty,
      details: swap.notes || swap.details || "",
      macros: swap.macros || (baseNutrition ? scaleNutritionForAmount(baseNutrition, formattedQty, baseAmount) : null),
      metadata,
      preserved_macro: swap.preserved_macro || null,
      category: swap.category || null,
      original_name: swap.original_name || null,
      replacement_grams: gramsValue,
      itemModel,
      nutritionAmount: swap.nutrition_amount || itemModel?.amount || null,
    };
    return applySwapTargetAdjustment(result, originalContext, baseNutrition, baseAmount || formattedQty);
  }
  const fallbackModel = swap.item_model || swap.itemModel || null;
  const fallbackMetadata = swap.metadata || fallbackModel?.metadata || null;
  const fallbackNutrition = normalizeNutrition(fallbackModel?.macros || null, fallbackMetadata);
  const fallbackAmount = swap.nutrition_amount || fallbackModel?.amount || fallbackMetadata?.serving_size || null;
  const normalized = {
    item: swap.item || "",
    qty: swap.qty ? formatAmountInGrams(swap.qty) : "",
    details: swap.details || "",
    macros: swap.macros || (fallbackNutrition ? scaleNutritionForAmount(fallbackNutrition, swap.qty, fallbackAmount) : null),
    metadata: fallbackMetadata,
    itemModel: fallbackModel,
    nutritionAmount: fallbackAmount,
    replacement_grams: null,
    preserved_macro: swap.preserved_macro || null,
    category: swap.category || null,
  };
  return applySwapTargetAdjustment(normalized, originalContext, fallbackNutrition, fallbackAmount);
};


const groupMealFoods = (foods = []) => {
  const grouped = {
    protein: [],
    carbs: [],
    fruitsVeggies: [],
    fats: [],
  };
  foods.forEach((food) => {
    const info = {};
    if (food.notes) info.details = food.notes;
    if (food.details && !info.details) info.details = food.details;
    const swapOptions = Array.isArray(food.swap_options) ? food.swap_options : [];
    const originalContext = buildFoodContext({ ...food, amount: food.qty ?? food.amount ?? food.quantity ?? null });
    const normalizedSwaps = swapOptions.map((swap) => normalizeSwapSuggestion(swap, originalContext)).filter(Boolean);
    if (normalizedSwaps.length) {
      info.swapOptions = normalizedSwaps;
    } else if (food.alt) {
      info.alt = food.alt;
    }
    if (food.nutrition_item && typeof food.nutrition_item === "object") {
      info.itemModel = food.nutrition_item;
      if (!info.nutrition && food.nutrition_item.macros) info.nutrition = food.nutrition_item.macros;
      if (!info.nutritionAmount && food.nutrition_item.amount) info.nutritionAmount = food.nutrition_item.amount;
      if (!info.metadata && food.nutrition_item.metadata) info.metadata = food.nutrition_item.metadata;
      if (!info.source && food.nutrition_item.source) info.source = food.nutrition_item.source;
    }
    if (food.info && typeof food.info === "object") {
      if (!info.details && food.info.details) info.details = food.info.details;
      if (!info.swapOptions && food.info.swapOptions) info.swapOptions = food.info.swapOptions;
      if (!info.alt && food.info.alt) info.alt = food.info.alt;
      if (!info.itemModel && food.info.itemModel) info.itemModel = food.info.itemModel;
      if (!info.nutrition && food.info.nutrition) info.nutrition = food.info.nutrition;
      if (!info.nutritionAmount && food.info.nutritionAmount) info.nutritionAmount = food.info.nutritionAmount;
      if (!info.metadata && food.info.metadata) info.metadata = food.info.metadata;
      if (!info.source && food.info.source) info.source = food.info.source;
    }
    if (food.nutrition_macros) info.nutrition = food.nutrition_macros;
    if (food.nutrition_amount) info.nutritionAmount = food.nutrition_amount;
    if (food.nutrition_source) info.source = food.nutrition_source;
    if (food.nutrition_metadata) info.metadata = food.nutrition_metadata;
    const entry = {
      name: food.item || "",
      amount: formatAmountInGrams(food.qty ?? food.amount),
      info: Object.keys(info).length ? info : undefined,
    };
    switch ((food.category || "").toLowerCase()) {
      case "protein":
        grouped.protein.push(entry);
        break;
      case "carb":
        grouped.carbs.push(entry);
        break;
      case "fruit_veg":
        grouped.fruitsVeggies.push(entry);
        break;
      case "fat":
        grouped.fats.push(entry);
        break;
      default:
        break;
    }
  });
  ["protein", "carbs", "fruitsVeggies", "fats"].forEach((key) => {
    if (!grouped[key].length) {
      grouped[key].push({ name: "None", amount: "-", info: undefined });
    }
  });
  return grouped;
};

const buildMealUi = (mealDay = null) => {
  if (!mealDay) return null;
  const total = mealDay.total || {};
  return {
    dayName: mealDay.day_name || "Daily",
    total: {
      calories: numberOrZero(total.calories),
      protein: numberOrZero(total.protein_g),
      carbs: numberOrZero(total.carbs_g),
      fats: numberOrZero(total.fats_g),
    },
    meals: (mealDay.meals || []).map((meal, idx) => ({
      id: meal.meal_number ?? idx + 1,
      mealNumber: meal.meal_number ?? idx + 1,
      label: meal.name || `Meal ${idx + 1}`,
      name: meal.name || `Meal ${idx + 1}`,
      timeWindow: meal.time_window || "",
      purpose: meal.purpose || "",
      notes: meal.notes || "",
      swapOptions: Array.isArray(meal.swap_options) ? meal.swap_options : [],
      macros: {
        protein: numberOrZero(meal.macros?.protein_g),
        carbs: numberOrZero(meal.macros?.carbs_g),
        fats: numberOrZero(meal.macros?.fats_g),
      },
      categories: groupMealFoods(meal.foods || []),
    })),
    guidelines: Array.isArray(mealDay.guidelines) ? mealDay.guidelines : [],
    supplementation: Array.isArray(mealDay.supplementation) ? mealDay.supplementation : [],
  };
};

const EMPTY_MEAL_PREFERENCES = {
  primaryGoal: "",
  bodyFat: "",
  mealCount: null,
  proteins: [],
  carbs: [],
  fats: [],
};

function Target({ label, value, unit, icon: Icon, variant }) {
  return (
    <div className={`target target-${variant}`}>
      <div className="target-top">
        {Icon && <Icon className="target-icon" />}
        <div className="target-value">
          {value}
          {unit}
        </div>
      </div>
      <div className="target-label">{label}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="meal-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
}

function MealItem({ item, onClick }) {
  if (!item) return null;
  const handleClick = () => {
    if (onClick) onClick(item);
  };
  return (
    <button type="button" className="meal-list-item" onClick={handleClick}>
      <div className="item-title">{item.name}</div>
      <div className="item-meta">{ensureAmountLabel(item.amount)}</div>
    </button>
  );
}

function InfoModal({
  open,
  onClose,
  planSummary,
  onGenerate,
  actionDisabled,
  mealPreferences,
  mealPreferencesLoading,
  mealPreferencesError,
  onSavePreferences,
  savingPreferences,
}) {
  const [showSuppDisclaimer, setShowSuppDisclaimer] = useState(false);
  const [activeTab, setActiveTab] = useState("plan");
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [prefDraft, setPrefDraft] = useState(null);
  const [prefLocalError, setPrefLocalError] = useState("");

  useEffect(() => {
    if (!open) {
      setActiveTab("plan");
      setEditingPreferences(false);
      setPrefDraft(null);
      setPrefLocalError("");
      setShowSuppDisclaimer(false);
    }
  }, [open]);

  const basePreferences = mealPreferences || EMPTY_MEAL_PREFERENCES;
  const rawPreferenceMealCount = Number(basePreferences.mealCount);
  const normalizedBaseMealCount =
    Number.isFinite(rawPreferenceMealCount) && rawPreferenceMealCount > 0
      ? rawPreferenceMealCount
      : planSummary?.meals?.length
        ? Math.min(Math.max(planSummary.meals.length, MEAL_COUNT_LIMITS.min), MEAL_COUNT_LIMITS.max)
        : null;
  const mergeOptionLists = (defaults = [], ...lists) => {
    const seen = new Set();
    const merged = [];
    const add = (val) => {
      const trimmed = (val || "").trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(trimmed);
    };
    (defaults || []).forEach(add);
    lists.forEach((list) => {
      if (Array.isArray(list)) list.forEach(add);
      else add(list);
    });
    return merged;
  };

  const currentPrimary = editingPreferences && prefDraft ? prefDraft.primaryGoal : basePreferences.primaryGoal;
  const mealCountGuidance = useMemo(
    () =>
      deriveMealCountGuidance({
        primaryGoal: currentPrimary,
        bodyFatEstimate: basePreferences.bodyFat,
      }),
    [currentPrimary, basePreferences.bodyFat]
  );

  const mealBandMin = mealCountGuidance?.rangeMin ?? MEAL_COUNT_LIMITS.min;
  const mealBandMax = mealCountGuidance?.rangeMax ?? MEAL_COUNT_LIMITS.max;
  const mealBandEditable = true; // Allow user override within 3-5 even if guidance band is fixed.

  const startEditing = () => {
    const seed = basePreferences || EMPTY_MEAL_PREFERENCES;
    setPrefDraft({
      primaryGoal: seed.primaryGoal || "",
      mealCount:
        normalizedBaseMealCount != null
          ? normalizedBaseMealCount
          : mealBandEditable
            ? mealBandMin
            : null,
      proteins: Array.isArray(seed.proteins) ? seed.proteins.slice() : [],
      carbs: Array.isArray(seed.carbs) ? seed.carbs.slice() : [],
      fats: Array.isArray(seed.fats) ? seed.fats.slice() : [],
    });
    setPrefLocalError("");
    setEditingPreferences(true);
  };

  const cancelEditing = () => {
    setEditingPreferences(false);
    setPrefDraft(null);
    setPrefLocalError("");
  };

  const togglePreferenceValue = (key, value) => {
    setPrefDraft((draft) => {
      if (!draft) return draft;
      const list = new Set(Array.isArray(draft[key]) ? draft[key] : []);
      const entry = (value || "").trim();
      if (!entry) return draft;
      if (list.has(entry)) list.delete(entry);
      else list.add(entry);
      return { ...draft, [key]: Array.from(list) };
    });
  };

  if (!open) return null;

  const guidelines = Array.isArray(planSummary?.guidelines) ? planSummary.guidelines : [];
  const supplementation = Array.isArray(planSummary?.supplementation) ? planSummary.supplementation : [];
  const hasGuidelines = guidelines.length > 0;
  const hasSupps = supplementation.length > 0;
  const hasDetails = hasGuidelines || hasSupps;
  const canGenerate = typeof onGenerate === "function";
  const closeDisclaimer = () => setShowSuppDisclaimer(false);
  const openDisclaimer = (event) => {
    event?.stopPropagation();
    setShowSuppDisclaimer(true);
  };
  const handleGenerateClick = () => {
    if (!canGenerate) return;
    if (typeof onClose === "function") {
      onClose();
    }
    onGenerate();
  };

  const handlePreferenceSubmit = async (event) => {
    event.preventDefault();
    if (!prefDraft || typeof onSavePreferences !== "function") return;
    const desiredMealCount = prefDraft.mealCount == null ? null : Number(prefDraft.mealCount);
    if (
      desiredMealCount != null &&
      (!Number.isFinite(desiredMealCount) || desiredMealCount < MEAL_COUNT_LIMITS.min || desiredMealCount > MEAL_COUNT_LIMITS.max)
    ) {
      setPrefLocalError(`Meal count must be between ${MEAL_COUNT_LIMITS.min} and ${MEAL_COUNT_LIMITS.max} meals/day. We'll scale macros to your choice.`);
      return;
    }

    const preferenceGroups = [
      { key: "proteins", label: "proteins" },
      { key: "carbs", label: "carbs" },
      { key: "fats", label: "fats" },
    ];
    const missingMinimum = preferenceGroups.filter(
      ({ key }) => !Array.isArray(prefDraft[key]) || prefDraft[key].length < MIN_PREFERENCE_SELECTIONS
    );
    if (missingMinimum.length) {
      const labels = missingMinimum.map(({ label }) => label);
      const finalLabel = labels.pop();
      const labelText = labels.length ? `${labels.join(", ")} and ${finalLabel}` : finalLabel;
      setPrefLocalError(`Select at least ${MIN_PREFERENCE_SELECTIONS} options for preferred ${labelText}.`);
      return;
    }

    const payload = {
      primaryGoal: prefDraft.primaryGoal,
      mealCount: prefDraft.mealCount == null ? null : prefDraft.mealCount,
      proteins: Array.isArray(prefDraft.proteins) ? prefDraft.proteins : [],
      carbs: Array.isArray(prefDraft.carbs) ? prefDraft.carbs : [],
      fats: Array.isArray(prefDraft.fats) ? prefDraft.fats : [],
    };
    try {
      await onSavePreferences(payload);
      if (canGenerate) {
        onGenerate();
      }
      setEditingPreferences(false);
      setPrefDraft(null);
      setPrefLocalError("");
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      setPrefLocalError(err?.message || "Unable to save preferences right now.");
    }
  };

  const renderPlanTab = () => (
    <>
      {hasDetails ? (
        <>
          {hasGuidelines && (
            <Section title="GUIDELINES">
              <ul className="guideline-list">
                {guidelines.map((line, idx) => (
                  <li key={`guide-${idx}`}>{line}</li>
                ))}
              </ul>
            </Section>
          )}
          {hasSupps && (
            <Section title="Supplementation">
              <div className="guideline-list">
                {supplementation.map((supp, idx) => (
                  <div
                    key={`supp-${idx}`}
                    style={{
                      background: "#f5f5f5",
                      borderRadius: 12,
                      padding: "12px 16px",
                      marginBottom: 10,
                    }}
                  >
                    <strong>{supp.name}</strong>
                    <p>Dosage: {supp.dosage}</p>
                    <p>Timing: {supp.timing}</p>
                    <p className="muted">Purpose: {supp.purpose}</p>
                  </div>
                ))}
              </div>
              <p className="supplement-disclaimer-text">
                supplement{" "}
                <a
                  href="#"
                  className="supplement-disclaimer-link"
                  onClick={(e) => {
                    e.preventDefault();
                    openDisclaimer(e);
                  }}
                >
                  disclaimer
                </a>
              </p>
            </Section>
          )}
        </>
      ) : (
        <p className="muted" style={{ marginBottom: 0 }}>
          No meal plan guidelines yet.
        </p>
      )}
      {/* {canGenerate && (
        <div className="meal-modal-block">
          <h4>Need a fresh plan?</h4>
          <p>If your schedule or nutrition targets changed, quickly regenerate a brand new plan below.</p>
          <button className="meal-reset-btn" onClick={handleGenerateClick} type="button" disabled={actionDisabled}>
            Generate new meal plan
          </button>
        </div>
      )} */}
    </>
  );

  const renderList = (items = []) =>
    items.length ? (
      <div className="preference-choice-list readonly">
        {items.map((item) => (
          <div key={item} className="preference-option selected readonly">
            <span className="preference-option-leading">✓</span>
            <span className="preference-option-label">{item}</span>
          </div>
        ))}
      </div>
    ) : (
      <p className="muted">Not provided yet.</p>
    );

  const renderPreferenceSelector = (label, key, defaults) => {
    if (!prefDraft) return null;
    const mergedOptions = mergeOptionLists(defaults, prefDraft[key], basePreferences[key]);
    const selectAll = () =>
      setPrefDraft((draft) => {
        if (!draft) return draft;
        return { ...draft, [key]: mergedOptions.slice() };
      });
    const clearAll = () =>
      setPrefDraft((draft) => {
        if (!draft) return draft;
        return { ...draft, [key]: [] };
      });
    const selectedSet = new Set(prefDraft[key] || []);
    return (
      <div className="preference-card">
        <div className="preference-card-header">
          <div className="label">{label}</div>
          <div className="preference-card-actions">
            <button type="button" onClick={selectAll}>
              Select all
            </button>
            <button type="button" onClick={clearAll}>
              Clear
            </button>
          </div>
        </div>
        <div className="preference-card-subtext">{PREFERENCE_SELECTION_SUBTEXT}</div>
        <div className="preference-choice-list editable">
          {mergedOptions.map((option) => {
            const selected = selectedSet.has(option);
            return (
              <button
                type="button"
                key={option}
                className={`preference-option ${selected ? "selected" : ""}`}
                onClick={() => togglePreferenceValue(key, option)}
              >
                <span className="preference-option-leading">{selected ? "✓" : "○"}</span>
                <span className="preference-option-label">{option}</span>
              </button>
            );
          })}
        </div>
        <div className="hint">
          Selected: {selectedSet.size} (min {MIN_PREFERENCE_SELECTIONS})
        </div>
      </div>
    );
  };

  const renderPreferencesTab = () => {
    if (mealPreferencesLoading && !editingPreferences) {
      return (
        <div className="meal-preferences-loading">
          <LoadingAnimation />
        </div>
      );
    }

    if (editingPreferences) {
      if (!prefDraft) return null;
      return (
        <form className="meal-preferences-form" onSubmit={handlePreferenceSubmit}>
          <div className="field">
            <label className="label">Primary goal</label>
            <select value={prefDraft.primaryGoal} onChange={(e) => setPrefDraft((draft) => ({ ...draft, primaryGoal: e.target.value }))}>
              <option value="">Select primary goal</option>
              <option value="Weight Loss">Weight Loss</option>
              <option value="Fatloss">Fat Loss</option>
              <option value="Muscle Size">Muscle Size</option>
              <option value="Weight Gain">Weight Gain</option>
              <option value="Health/Longevitiy">Health/Longevitiy</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Meal count</label>
            <>
              <input
                type="number"
                min={MEAL_COUNT_LIMITS.min}
                max={MEAL_COUNT_LIMITS.max}
                value={prefDraft.mealCount ?? ""}
                placeholder={`${MEAL_COUNT_LIMITS.min}-${MEAL_COUNT_LIMITS.max}`}
                onChange={(e) =>
                  setPrefDraft((draft) => ({
                    ...draft,
                    mealCount:
                      e.target.value === ""
                        ? null
                        : Math.max(MEAL_COUNT_LIMITS.min, Math.min(MEAL_COUNT_LIMITS.max, Number(e.target.value))),
                  }))
                }
              />
              <p className="meal-preferences-hint">
                Goal guidance: {mealBandMin}-{mealBandMax} meals/day. You can pick {MEAL_COUNT_LIMITS.min}-{MEAL_COUNT_LIMITS.max}; we'll scale macros per meal to stay on target.
              </p>
            </>
            <p className="meal-preferences-hint muted">If blank, we'll use the guided range for first generation. Edits after generation keep total macros but adjust per-meal portions.</p>
          </div>
          {renderPreferenceSelector("Preferred proteins", "proteins", PROTEIN_OPTIONS)}
          {renderPreferenceSelector("Preferred carbs", "carbs", CARB_OPTIONS)}
          {renderPreferenceSelector("Preferred fats", "fats", FAT_OPTIONS)}
          {prefLocalError && (
            <p className="error-text" style={{ marginTop: 4 }}>
              {prefLocalError}
            </p>
          )}
          <div className="meal-preferences-actions">
            <button type="button" className="meal-btn secondary" onClick={cancelEditing} disabled={savingPreferences}>
              Cancel
            </button>
            <button type="submit" className="meal-btn primary" disabled={savingPreferences}>
              {savingPreferences ? "Saving..." : "Save preferences"}
            </button>
          </div>
          <p className="meal-preferences-hint muted">Saving automatically regenerates your meal plan using these preferences.</p>
        </form>
      );
    }

    return (
      <div className="meal-preferences-summary">
        {mealPreferencesError && (
          <p className="error-text" style={{ marginBottom: 12 }}>
            {mealPreferencesError}
          </p>
        )}
        <div className="preference-row">
          <span className="preference-label">Primary goal</span>
          <span className="preference-value">{basePreferences.primaryGoal || "Not set"}</span>
        </div>
        <div className="preference-row">
          <span className="preference-label">Current meal count</span>
          <span className="preference-value">
            {normalizedBaseMealCount != null
              ? `${normalizedBaseMealCount} meals/day`
              : mealBandMin === mealBandMax
                ? `${mealBandMin} (auto)`
                : `Let Cole decide (${mealBandMin}-${mealBandMax})`}
          </span>
        </div>
        <div className="preference-row">
          <span className="preference-label">Meal count range</span>
          <span className="preference-value">
            {mealBandMin === mealBandMax ? `${mealBandMin} meals/day` : `${mealBandMin}-${mealBandMax} meals/day`}
          </span>
        </div>
        <p className="meal-preferences-hint muted">
          Meal count range is determined by your goals and body-fat context.
        </p>
        <div className="preference-list-block">
          <span className="preference-label">Preferred proteins</span>
          <span className="preference-subtext">{PREFERENCE_SELECTION_SUBTEXT}</span>
          {renderList(basePreferences.proteins)}
        </div>
        <div className="preference-list-block">
          <span className="preference-label">Preferred carbs</span>
          <span className="preference-subtext">{PREFERENCE_SELECTION_SUBTEXT}</span>
          {renderList(basePreferences.carbs)}
        </div>
        <div className="preference-list-block">
          <span className="preference-label">Preferred fats</span>
          <span className="preference-subtext">{PREFERENCE_SELECTION_SUBTEXT}</span>
          {renderList(basePreferences.fats)}
        </div>
        <button type="button" className="meal-btn primary" onClick={startEditing} disabled={mealPreferencesLoading}>
          Edit preferences
        </button>
        <p className="meal-preferences-hint muted">Need to tweak foods or goals? Save changes and we’ll refresh your plan automatically.</p>
      </div>
    );
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content meal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="meal-modal-header">
            <h3>Plan Info</h3>
            <CancelIcon className="cancel-icon" onClick={onClose} />
          </div>
          <div className="meal-modal-tabs">
            <button
              type="button"
              className={`meal-modal-tab ${activeTab === "plan" ? "active" : ""}`}
              onClick={() => setActiveTab("plan")}
            >
              Plan details
            </button>
            <button
              type="button"
              className={`meal-modal-tab ${activeTab === "preferences" ? "active" : ""}`}
              onClick={() => setActiveTab("preferences")}
            >
              My Preferences
            </button>
          </div>
          {activeTab === "plan" ? renderPlanTab() : renderPreferencesTab()}
        </div>
      </div>

      {showSuppDisclaimer && (
        <div className="supplement-disclaimer-overlay" onClick={closeDisclaimer}>
          <div className="supplement-disclaimer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="supplement-disclaimer-header">
              <h3>Supplement Disclaimer</h3>
              <button type="button" className="supplement-disclaimer-close" onClick={closeDisclaimer} aria-label="Close supplement disclaimer">
                <CancelIcon className="cancel-icon" />
              </button>
            </div>
            <div className="supplement-disclaimer-body">
              <p>
                The information provided in this plan, including supplement suggestions, is for informational and general-wellness purposes only. It is not
                medical advice and should not be used to diagnose, treat, cure, or prevent any disease. These recommendations are not a substitute for
                professional medical guidance or individualized care.
              </p>
              <p>
                Always consult with a licensed physician or qualified healthcare professional before beginning any supplement, dietary, or lifestyle regimen,
                especially if you have pre-existing health conditions, take prescription or over-the-counter medications, are pregnant or nursing, or have any
                concerns regarding your personal health.
              </p>
              <p>Use of this information is at your own discretion and risk.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ItemModal({ open, onClose, item, onSwap, onSeeAllSwaps }) {
  const swapOptions = Array.isArray(item?.info?.swapOptions) ? item.info.swapOptions : [];
  const featuredSwaps = useMemo(
    () => (open ? pickRandomSubset(swapOptions, 3) : []),
    [open, swapOptions, item?.id, item?.name],
  );
  if (!open || !item) return null;
  const itemModel = item?.info?.itemModel || null;
  const metadata = item?.info?.metadata || itemModel?.metadata || null;
  const baseNutrition = normalizeNutrition(item?.info?.nutrition || itemModel?.macros || null, metadata);
  const baseServingAmount =
    item?.info?.nutritionAmount || itemModel?.amount || metadata?.serving_size || null;
  const nutrientInfo = scaleNutritionForAmount(baseNutrition, item.amount, baseServingAmount);
  const nutritionEntries = nutrientInfo
    ? [
      nutrientInfo.calories != null ? { key: "calories", label: "Calories", value: `${numberOrZero(nutrientInfo.calories)} kcal` } : null,
      nutrientInfo.protein_g != null ? { key: "protein", label: "Protein", value: `${numberOrZero(nutrientInfo.protein_g)} g` } : null,
      nutrientInfo.carbs_g != null ? { key: "carbs", label: "Carbs", value: `${numberOrZero(nutrientInfo.carbs_g)} g` } : null,
      nutrientInfo.fats_g != null ? { key: "fats", label: "Fats", value: `${numberOrZero(nutrientInfo.fats_g)} g` } : null,
    ].filter(Boolean)
    : [];
  const metadataEntries = buildMetadataEntries(metadata);
  const canSwap = typeof onSwap === "function" && swapOptions.length > 0;
  const canSeeAll = typeof onSeeAllSwaps === "function" && swapOptions.length > 0;
  const displayName = item.name || itemModel?.name || "Food Item";
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content meal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meal-modal-header">
          <h3>{displayName}</h3>
          <CancelIcon className="cancel-icon" onClick={onClose} />
        </div>
        <div className="meal-item-details">
          {item.amount && (
            <p>
              <strong>Amount:</strong> {ensureAmountLabel(item.amount)}
            </p>
          )}
          {nutritionEntries.length > 0 && (
            <div className="meal-item-nutrition">
              <strong>Nutrient info:</strong>
              <div className="nutrition-pill-row">
                {nutritionEntries.map((entry) => (
                  <span key={`${displayName}-${entry.label}`} className={`nutrition-pill nutrition-pill-${entry.key || "default"}`}>
                    {entry.label}: {entry.value}
                  </span>
                ))}
              </div>
            </div>
          )}
          {metadataEntries.length > 0 && (
            <div className="meal-item-metadata">
              <strong>Full nutrition profile:</strong>
              <div className="metadata-grid">
                {metadataEntries.map((entry) => (
                  <div key={`${displayName}-${entry.key}`} className="metadata-grid-row">
                    <span className="metadata-label">{entry.label}</span>
                    <span className="metadata-value">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {item?.info?.alt && (
            <p>
              <strong>Alternatives:</strong> {item.info.alt}
            </p>
          )}
          {canSwap ? (
            <div className="meal-item-swaps">
              <div className="meal-item-swaps-header">
                <strong>Swap options:</strong>
                {canSeeAll && (
                  <button
                    type="button"
                    className="swap-see-more"
                    onClick={() => onSeeAllSwaps(item)}
                  >
                    See more
                  </button>
                )}
              </div>
              {swapOptions.length > 3 && (
                <p className="swap-options-hint">
                  Showing 3 random picks out of {swapOptions.length}.
                </p>
              )}
              <p className="swap-macro-note">Macros shown are for each swap amount listed.</p>
              <div className="swap-pill-row">
                {featuredSwaps.map((swap, idx) => (
                  <button
                    key={`swap-${idx}`}
                    type="button"
                    className="swap-pill"
                    onClick={() => onSwap(item, swap)}
                  >
                    <span className="swap-pill-title">{swap.item}</span>
                    {swap.qty && <span className="swap-pill-qty">{ensureAmountLabel(swap.qty)}</span>}
                    {swap.macros && (
                      <span className="swap-pill-macros">
                        {formatMacroSummaryWithAmount(swap.macros, deriveSwapAmountLabel(swap))}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="muted swap-unavailable">No swap options available for this item yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SwapOptionsModal({ open, item, swaps, loading, error, onClose, onSwap, onRetry }) {
  if (!open || !item) return null;
  const displayName = item.name || item?.info?.itemModel?.name || "this item";
  const handleSelectSwap = (swap) => {
    if (typeof onSwap === "function") {
      onSwap(item, swap);
    }
    if (typeof onClose === "function") {
      onClose();
    }
  };
  const hasSwaps = Array.isArray(swaps) && swaps.length > 0;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content meal-modal swap-options-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meal-modal-header">
          <h3>All swap options</h3>
          <CancelIcon className="cancel-icon" onClick={onClose} />
        </div>
        <p className="swap-options-intro">
          Pick a new option for <strong>{displayName}</strong>.
        </p>
        <p className="swap-macro-note">Macros below are calculated for each listed swap amount.</p>
        {loading ? (
          <div className="swap-options-loading">
            <LoadingAnimation />
          </div>
        ) : hasSwaps ? (
          <div className="swap-options-list">
            {swaps.map((swap, idx) => (
              <button
                key={`swap-option-${idx}-${swap.item || "swap"}`}
                type="button"
                className="swap-pill"
                onClick={() => handleSelectSwap(swap)}
              >
                <span className="swap-pill-title">{swap.item}</span>
                {swap.qty && <span className="swap-pill-qty">{ensureAmountLabel(swap.qty)}</span>}
                {swap.macros && (
                  <span className="swap-pill-macros">
                    {formatMacroSummaryWithAmount(swap.macros, deriveSwapAmountLabel(swap))}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <p className="muted swap-options-empty">No additional swaps available yet. Check back soon.</p>
        )}
        {error && (
          <p className="error-text swap-options-error">
            {error}
            {typeof onRetry === "function" && (
              <button type="button" className="swap-retry" onClick={() => onRetry(item)}>
                Try again
              </button>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

function SwapConfirmationModal({ open, originalItem, swap, onConfirm, onCancel, saving, error }) {
  if (!open || !originalItem || !swap) return null;
  const metadata = originalItem?.info?.metadata || null;
  const baseNutrition = normalizeNutrition(originalItem?.info?.nutrition || null, metadata);
  const baseServingAmount = originalItem?.info?.nutritionAmount || metadata?.serving_size || null;
  const originalMacros = scaleNutritionForAmount(baseNutrition, originalItem.amount, baseServingAmount);
  const replacementMacros = swap.macros || null;
  const originalAmountLabel = ensureAmountLabel(originalItem.amount);
  const swapAmount = deriveSwapAmountLabel(swap);
  const formatMacroSummary = (macros, amountLabel) =>
    macros ? formatMacroSummaryWithAmount(macros, amountLabel) : "N/A";

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content meal-modal swap-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meal-modal-header">
          <h3>Confirm swap</h3>
          <CancelIcon className="cancel-icon" onClick={onCancel} />
        </div>
        {saving ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <LoadingAnimation />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: 16 }}>
              Replace <strong>{originalItem.name}</strong> with <strong>{swap.item}</strong> and update your plan?
            </p>
            <p className="swap-macro-note" style={{ marginTop: -8, marginBottom: 12 }}>
              Macro totals below reflect the amounts shown for each item.
            </p>
            <div className="swap-compare">
              <div className="swap-compare-card">
                <strong>Current</strong>
                <span className="swap-compare-name">{originalItem.name}</span>
                <span className="swap-compare-amount">{ensureAmountLabel(originalItem.amount)}</span>
                <span className="swap-compare-macros">{formatMacroSummary(originalMacros, originalAmountLabel)}</span>
              </div>
              <div className="swap-compare-card swap-compare-card-new">
                <strong>Swap</strong>
                <span className="swap-compare-name">{swap.item}</span>
                <span className="swap-compare-amount">{swapAmount}</span>
                <span className="swap-compare-macros">{formatMacroSummary(replacementMacros, swapAmount)}</span>
              </div>
            </div>
            {error && (
              <p className="error-text" style={{ marginTop: 8 }}>
                {error}
              </p>
            )}
            <div className="swap-confirm-actions">
              <button type="button" className="secondary-btn" onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="primary-btn" onClick={onConfirm}>
                Confirm swap
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MealPage() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedMealIdx, setExpandedMealIdx] = useState(-1);
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [swapExplorerItem, setSwapExplorerItem] = useState(null);
  const [swapExplorerSwaps, setSwapExplorerSwaps] = useState([]);
  const [swapExplorerLoading, setSwapExplorerLoading] = useState(false);
  const [swapExplorerError, setSwapExplorerError] = useState("");
  const [error, setError] = useState("");
  const [pendingSwap, setPendingSwap] = useState(null);
  const [savingSwap, setSavingSwap] = useState(false);
  const [swapError, setSwapError] = useState("");
  const [mealPreferences, setMealPreferences] = useState(null);
  const [mealPreferencesLoading, setMealPreferencesLoading] = useState(true);
  const [mealPreferencesError, setMealPreferencesError] = useState("");
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const loaderTimerRef = useRef(null);
  const [trainingDays, setTrainingDays] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState("");

  const meals = useMemo(() => (plan?.meals?.length ? plan.meals : []), [plan]);
  const isPlanBusy = loading || generating || resetting;
  const planReady = !!plan && !isPlanBusy;

  const loadMealPreferences = useCallback(async () => {
    setMealPreferencesLoading(true);
    setMealPreferencesError("");
    try {
      const prefs = await fetchMealPreferences();
      setMealPreferences(prefs);
    } catch (err) {
      setMealPreferences(null);
      setMealPreferencesError(err?.message || "Unable to load your preferences.");
    } finally {
      setMealPreferencesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMealPreferences();
  }, [loadMealPreferences]);


  const loadTrainingDays = useCallback(async () => {
    setTrainingLoading(true);
    setTrainingError("");
    try {
      const { supabase } = await import("../supabaseClient");
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("Not authenticated");
      const { data: prog, error: progErr } = await supabase
        .from("client_programs")
        .select("id, training_days")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      let td = prog?.training_days ?? null;
      if (!td && prog?.id) {
        const { count: dayCount } = await supabase
          .from("workout_days")
          .select("id", { count: "exact", head: true })
          .eq("program_id", prog.id);
        if (typeof dayCount === "number" && dayCount > 0) {
          td = dayCount;
        }
      }
      if (!td) {
        const { data: wp, error: wpErr } = await supabase
          .from("workout_plans")
          .select("training_days, plan")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (wpErr) throw wpErr;
        td = wp?.training_days ?? null;
        if (!td && wp?.plan?.days && Array.isArray(wp.plan.days)) {
          td = wp.plan.days.length || null;
        }
      }
      if (progErr) throw progErr;
      setTrainingDays(td);
      if (!td) {
        setTrainingError("Create a workout plan to enable meal plan generation.");
      } else {
        setTrainingError("");
        setError("");
        console.info("[MealPage] training days resolved", {
          trainingDays: td,
          progTrainingDays: prog?.training_days ?? null,
          progId: prog?.id ?? null,
        });
      }
    } catch (err) {
      setTrainingDays(null);
      setTrainingError("Create a workout plan to enable meal plan generation.");
      console.warn("[MealPage] training days lookup failed", err?.message);
    } finally {
      setTrainingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrainingDays();
  }, [loadTrainingDays]);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { supabase } = await import("../supabaseClient");
      const { data: sessionData } = await supabase.auth.getSession();
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("Not authenticated");
      const res = await apiFetch(
        `/meal-plan?userId=${encodeURIComponent(userId)}&accessToken=${encodeURIComponent(
          sessionData?.session?.access_token || ""
        )}`
      );
      const json = await res.json();
      if (json?.meal_day) {
        setPlan(buildMealUi(json.meal_day));
      } else if (json?.ui) {
        setPlan(json.ui);
      } else {
        setPlan(null);
      }
    } catch (err) {
      setPlan(null);
      setError("Unable to load your meal plan right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!trainingDays) {
      setError("Create a workout plan first so we know your training days.");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const { supabase } = await import("../supabaseClient");
      const { data: sessionData } = await supabase.auth.getSession();
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("Not authenticated");
      const res = await apiFetch("/generate-meal-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, accessToken: sessionData?.session?.access_token || "" }),
      });
      let json = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }
      if (!res.ok) {
        const message = json?.error || "Unable to generate a meal plan right now. Please try again soon.";
        console.warn("[MealPage] generate-meal-plan failed", { status: res.status, message, json });
        setError(message);
        return;
      }
      if (json?.meal_day) {
        setPlan(buildMealUi(json.meal_day));
      } else if (json?.ui) {
        setPlan(json.ui);
      }
      await fetchPlan();
    } catch (err) {
      setError("Unable to generate a meal plan right now. Please try again soon.");
    } finally {
      setGenerating(false);
    }
  }, [fetchPlan, trainingDays]);

  const handleResetAndGenerate = useCallback(async () => {
    setResetting(true);
    try {
      await handleGenerate();
    } finally {
      setResetting(false);
    }
  }, [handleGenerate]);

  const handleSavePreferences = useCallback(async (payload) => {
    setSavingPreferences(true);
    setMealPreferencesError("");
    try {
      const next = await updateMealPreferences(payload);
      setMealPreferences(next);
      return next;
    } catch (err) {
      const message = err?.message || "Unable to save your preferences right now.";
      setMealPreferencesError(message);
      throw new Error(message);
    } finally {
      setSavingPreferences(false);
    }
  }, []);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  useEffect(() => {
    setExpandedMealIdx(-1);
  }, [plan?.dayName]);

  useEffect(() => {
    if (!selectedItem) {
      setSwapExplorerItem(null);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (!swapExplorerItem) {
      setSwapExplorerSwaps([]);
      setSwapExplorerError("");
      setSwapExplorerLoading(false);
    }
  }, [swapExplorerItem]);

  const dailyTotals = planReady ? plan?.total || null : null;
  const visibleMeals = planReady ? meals : [];
  const hasPlanData = meals.length > 0;
  const generatingFirstTime = generating && !hasPlanData;
  const showInitialLoader = loading && !hasPlanData && !generatingFirstTime;
  const showLoadingMessages = generatingFirstTime;
  const showPlanInfoButton = planReady;

  useEffect(() => {
    if (showLoadingMessages && MEAL_LOADING_STEPS.length) {
      setLoadingMessage(MEAL_LOADING_STEPS[0]);
      let index = 1;
      loaderTimerRef.current = setInterval(() => {
        setLoadingMessage(() => {
          const next = MEAL_LOADING_STEPS[index % MEAL_LOADING_STEPS.length];
          index += 1;
          return next;
        });
      }, 2200);
    } else {
      if (loaderTimerRef.current) {
        clearInterval(loaderTimerRef.current);
        loaderTimerRef.current = null;
      }
      setLoadingMessage("");
    }
    return () => {
      if (loaderTimerRef.current) {
        clearInterval(loaderTimerRef.current);
        loaderTimerRef.current = null;
      }
    };
  }, [showLoadingMessages]);

  const loadSwapExplorerOptions = useCallback(
    async (item, fallbackSwaps = []) => {
      if (!item) return;
      setSwapExplorerLoading(true);
      setSwapExplorerError("");
      try {
        const { supabase } = await import("../supabaseClient");
        const { data: sessionData } = await supabase.auth.getSession();
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth?.user?.id;
        if (!userId) throw new Error("Not authenticated");
        const payload = {
          userId,
          accessToken: sessionData?.session?.access_token || "",
          mealNumber: item.mealNumber,
          category: PLAN_CATEGORY_FROM_UI[item.categoryKey] || item.categoryKey,
          categoryIndex: item.categoryIndex,
          limit: 15,
        };
        const res = await apiFetch("/meal-plan/swap-options", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        let json = null;
        try {
          json = await res.json();
        } catch {
          json = null;
        }
        if (!res.ok) {
          const message = json?.error || "Unable to load swap options right now.";
          throw new Error(message);
        }
        const nextSwaps = Array.isArray(json?.swaps) ? json.swaps.filter(Boolean) : [];
        const originalContext = buildFoodContext(item);
        const normalizedSwaps = nextSwaps.map((swap) => normalizeSwapSuggestion(swap, originalContext)).filter(Boolean);
        setSwapExplorerSwaps(normalizedSwaps.length ? normalizedSwaps : fallbackSwaps);
      } catch (err) {
        setSwapExplorerError(err.message || "Unable to load swap options right now.");
        setSwapExplorerSwaps((current) => (current && current.length ? current : fallbackSwaps));
      } finally {
        setSwapExplorerLoading(false);
      }
    },
    [],
  );

  const handleRequestSwap = useCallback((originalItem, swap) => {
    if (!originalItem || !swap) return;
    setPendingSwap({ originalItem, swap });
    setSwapError("");
  }, []);

  const handleOpenSwapExplorer = useCallback((item) => {
    if (!item) return;
    const fallbackSwaps = Array.isArray(item?.info?.swapOptions) ? item.info.swapOptions : [];
    setSwapExplorerItem(item);
    setSwapExplorerSwaps(fallbackSwaps);
    loadSwapExplorerOptions(item, fallbackSwaps);
  }, [loadSwapExplorerOptions]);

  const handleCloseSwapExplorer = useCallback(() => {
    setSwapExplorerItem(null);
    setSwapExplorerSwaps([]);
    setSwapExplorerError("");
    setSwapExplorerLoading(false);
  }, []);

  const handleSwapFromExplorer = useCallback(
    (item, swap) => {
      handleRequestSwap(item, swap);
      setSwapExplorerItem(null);
      setSwapExplorerSwaps([]);
      setSwapExplorerError("");
    },
    [handleRequestSwap],
  );

  const handleRefreshSwapExplorer = useCallback(() => {
    if (!swapExplorerItem) return;
    const fallbackSwaps = Array.isArray(swapExplorerItem?.info?.swapOptions) ? swapExplorerItem.info.swapOptions : [];
    loadSwapExplorerOptions(swapExplorerItem, fallbackSwaps);
  }, [swapExplorerItem, loadSwapExplorerOptions]);

  const handleCancelSwap = useCallback(() => {
    if (savingSwap) return;
    setPendingSwap(null);
    setSwapError("");
  }, [savingSwap]);

  const handleConfirmSwap = useCallback(async () => {
    if (!pendingSwap) return;
    setSavingSwap(true);
    setSwapError("");
    try {
      const { supabase } = await import("../supabaseClient");
      const { data: sessionData } = await supabase.auth.getSession();
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) throw new Error("Not authenticated");
      const payload = {
        userId,
        accessToken: sessionData?.session?.access_token || "",
        mealNumber: pendingSwap.originalItem?.mealNumber,
        category:
          PLAN_CATEGORY_FROM_UI[pendingSwap.originalItem?.categoryKey] ||
          pendingSwap.originalItem?.categoryKey,
        categoryIndex: pendingSwap.originalItem?.categoryIndex,
        swap: pendingSwap.swap,
      };
      const res = await apiFetch("/meal-plan/swap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error("Unable to save swap right now.");
      }
      const json = await res.json();
      if (json?.meal_day) {
        setPlan(buildMealUi(json.meal_day));
      } else if (json?.ui) {
        setPlan(json.ui);
      }
      setPendingSwap(null);
      setSelectedItem(null);
    } catch (err) {
      setSwapError(err.message || "Unable to save swap right now.");
    } finally {
      setSavingSwap(false);
    }
  }, [pendingSwap]);

  return (
    <>
      {showInitialLoader && <LoadingAnimation fullScreen />}
      <NavBar />
      <main className="web-view">
        {generatingFirstTime ? (
          <MealLoadingOverlay message={loadingMessage} />
        ) : (
          <div className="container">
            <div className="card">
              <div className="card-header meal-header">
                <div>
                  <h3>
                    Daily Diet Plan
                    {planReady && plan?.dayName && plan.dayName !== "Daily" ? ` - ${plan.dayName}` : ""}
                  </h3>
                  <p>Your personalized daily nutrition plan</p>
                </div>
                <div className="meal-header-actions">
                  {showPlanInfoButton && (
                    <button className="info-button" onClick={() => setInfoOpen(true)} aria-label="Plan Info">
                      <InfoIcon className="icon" />
                    </button>
                  )}
                </div>
              </div>
              {isPlanBusy && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                  <LoadingAnimation />
                </div>
              )}
              {error && (
                <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: "#2b1a1a", color: "#ffb4b4" }}>
                  {error}
                </div>
              )}
              {dailyTotals ? (
                <>
                  <p className="macro-title">Daily Macro Totals</p>
                  <div className="targets">
                    <Target label="Calories" value={dailyTotals.calories} unit="" icon={CalorieIcon} variant="calories" />
                    <Target label="Protein" value={dailyTotals.protein} unit="g" icon={ProteinIcon} variant="protein" />
                    <Target label="Carbs" value={dailyTotals.carbs} unit="g" icon={CarbIcon} variant="carbs" />
                    <Target label="Fats" value={dailyTotals.fats} unit="g" icon={FatIcon} variant="fats" />
                  </div>
                </>
              ) : (
                !isPlanBusy && (
                  <div className="empty-tracker">
                    <p>No meal plan on deck yet. Once your plan has been created there are no excuses, just execution.</p>
                  </div>
                )
              )}
            </div>

            {isPlanBusy ? null : planReady && visibleMeals.length ? (
              visibleMeals.map((meal, idx) => {
                const expanded = expandedMealIdx === idx;
                const estCalories = meal.calories ?? mealCaloriesFromMacros(meal.macros || {});
                const macroSummary = mealMacroSummary(meal.macros);
                const mealNotes = (meal.notes || "").trim();
                const buildItemMeta = (entry, categoryKey, categoryLabel, categoryIndex) => ({
                  ...entry,
                  amount: ensureAmountLabel(entry.amount),
                  mealId: meal.id,
                  mealIndex: idx,
                  mealNumber: meal.mealNumber,
                  categoryKey,
                  categoryLabel,
                  categoryIndex,
                });
                return (
                  <div key={meal.id} className="card meal-card">
                    {!expanded ? (
                      <div className="meal-collapsed" onClick={() => setExpandedMealIdx(idx)} role="button" aria-expanded={expanded}>
                        <div className="meal-collapsed-text">
                          <div className="meal-title">MEAL {meal.mealNumber}</div>
                          <div className="meal-name">{meal.label || meal.name}</div>
                          <div className="meal-kcal">
                            <span className="meal-kcal-label">est</span>
                            <span className="meal-kcal-pill">{estCalories} kcal</span>
                            {macroSummary.length > 0 && (
                              <span className="meal-macro-summary">
                                {macroSummary.map((macro) => (
                                  <span key={`${meal.id}-${macro.key}`} className={`meal-macro-pill macro-${macro.key}`}>
                                    {macro.value}g {macro.short}
                                  </span>
                                ))}
                              </span>
                            )}
                          </div>
                          <button
                            className="see-more-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedMealIdx(idx);
                            }}
                          >
                            see more info
                          </button>
                        </div>
                        <MealPlanIcon className="meal-thumb-icon" aria-hidden="true" />
                      </div>
                    ) : (
                      <div className="meal-expanded">
                        <div className="meal-expanded-header">
                          <div>
                            <div className="meal-title">MEAL {meal.mealNumber}</div>
                            <div className="meal-name">{meal.name || meal.label}</div>
                            <div className="meal-kcal">
                              <span className="meal-kcal-label">est</span>
                              <span className="meal-kcal-pill">{estCalories} kcal</span>
                              {macroSummary.length > 0 && (
                                <span className="meal-macro-summary">
                                  {macroSummary.map((macro) => (
                                    <span key={`${meal.id}-expanded-${macro.key}`} className={`meal-macro-pill macro-${macro.key}`}>
                                      {macro.value}g {macro.short}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className="collapse-btn" aria-label="collapse" onClick={() => setExpandedMealIdx(-1)}>
                            <CancelIcon className="cancel-icon" />
                          </button>
                        </div>

                        <Section title="PROTEIN">
                          <div className="meal-list">
                            {meal.categories.protein.map((it, pIdx) => (
                              <MealItem
                                key={`protein-${meal.id}-${pIdx}`}
                                item={buildItemMeta(it, "protein", "Protein", pIdx)}
                                onClick={setSelectedItem}
                              />
                            ))}
                          </div>
                        </Section>

                        <Section title="CARBS">
                          <div className="meal-list">
                            {meal.categories.carbs.map((it, cIdx) => (
                              <MealItem
                                key={`carb-${meal.id}-${cIdx}`}
                                item={buildItemMeta(it, "carbs", "Carbs", cIdx)}
                                onClick={setSelectedItem}
                              />
                            ))}
                          </div>
                        </Section>

                        <Section title="FRUITS/VEGGIES">
                          <div className="meal-list">
                            {meal.categories.fruitsVeggies.map((it, fvIdx) => (
                              <MealItem
                                key={`fv-${meal.id}-${fvIdx}`}
                                item={buildItemMeta(it, "fruitsVeggies", "Fruits/Veggies", fvIdx)}
                                onClick={setSelectedItem}
                              />
                            ))}
                          </div>
                        </Section>

                        <Section title="FATS">
                          <div className="meal-list">
                            {meal.categories.fats.map((it, fIdx) => (
                              <MealItem
                                key={`fat-${meal.id}-${fIdx}`}
                                item={buildItemMeta(it, "fats", "Fats", fIdx)}
                                onClick={setSelectedItem}
                              />
                            ))}
                          </div>
                        </Section>

                        {Array.isArray(meal.swapOptions) && meal.swapOptions.length > 0 && (
                          <Section title="SWAP OPTIONS">
                            <ul className="guideline-list">
                              {meal.swapOptions.map((swap, sIdx) => (
                                <li key={`swap-${meal.id}-${sIdx}`}>{swap}</li>
                              ))}
                            </ul>
                          </Section>
                        )}

                        {mealNotes && (
                          <Section title="NOTES">
                            <div className="meal-notes">{mealNotes}</div>
                          </Section>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="card meal-card empty-plan-card">
                <img className="empty-plan-illustration" src={mealPlanIllustration} alt="Meal plan preview" />
                <div className="empty-plan-card-content">
                  <h4 className="empty-plan-card-title">Ready to get your personalized meal plan?</h4>
                  <p className="muted empty-plan-card-copy">
                    Tap below to generate your personalized plan in seconds.
                  </p>
                  <p className="muted empty-plan-card-copy">
                    Your coach will review your plan within the next 24hrs to ensure plan accuracy in alignment with your goals.
                  </p>
                  {generating && (
                    <div className="empty-plan-loader">
                      <LoadingAnimation />
                    </div>
                  )}
                  <button
                    className="btn-primary empty-plan-cta"
                    onClick={handleGenerate}
                    disabled={generating || trainingLoading || !trainingDays}
                  >
                    {generating ? "Generating..." : "Generate plan"}
                  </button>
                  {!trainingLoading && !trainingDays && (
                    <p className="muted" style={{ marginTop: 8 }}>
                      Create a workout plan first so we can use your weekly training days.
                    </p>
                  )}
                  {trainingLoading && (
                    <p className="muted" style={{ marginTop: 8 }}>
                      Checking your workout plan...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      <InfoModal
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        planSummary={plan}
        onGenerate={handleResetAndGenerate}
        actionDisabled={generating || resetting || loading}
        mealPreferences={mealPreferences}
        mealPreferencesLoading={mealPreferencesLoading}
        mealPreferencesError={mealPreferencesError}
        onSavePreferences={handleSavePreferences}
        savingPreferences={savingPreferences}
      />
      <ItemModal
        open={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
        onSwap={handleRequestSwap}
        onSeeAllSwaps={handleOpenSwapExplorer}
      />
      <SwapOptionsModal
        open={!!swapExplorerItem}
        item={swapExplorerItem}
        swaps={swapExplorerSwaps}
        loading={swapExplorerLoading}
        error={swapExplorerError}
        onClose={handleCloseSwapExplorer}
        onSwap={handleSwapFromExplorer}
        onRetry={handleRefreshSwapExplorer}
      />
      <SwapConfirmationModal
        open={!!pendingSwap}
        originalItem={pendingSwap?.originalItem || null}
        swap={pendingSwap?.swap || null}
        onConfirm={handleConfirmSwap}
        onCancel={handleCancelSwap}
        saving={savingSwap}
        error={swapError}
      />
    </>
  );
}
